import React, { useEffect } from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ScrollToTop } from './components/ScrollToTop';
// import { useAuth } from './components/AuthProviderWrapper';
import './styles/globals.css';

// Import components directly without lazy loading for iOS
import { SecureConfigProvider } from './components/SecureConfigProvider';
import { AuthProviderWrapper } from './components/AuthProviderWrapper';
import { DataProvider } from './components/DataProvider';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { AppLayout } from './components/AppLayout';
import { IOSTabLayout } from './components/navigation/IOSTabLayout';

// Direct imports for iOS compatibility (no lazy loading)
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import SignupPage from './pages/auth/SignupPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';
import TwoFactorPage from './pages/auth/TwoFactorPage';
// Removed direct import of AuthRoutes to avoid conflict with lazy loading in App.tsx

import RecipeList from './components/RecipeList';
import CreateRecipePage from './pages/CreateRecipePage';
import SearchPage from './pages/SearchPage';
import MealPlannerPage from './pages/MealPlannerPage';
import ShoppingListPage from './pages/ShoppingListPage';
import FavoritesPage from './pages/FavoritesPage';
import ProfilePage from './pages/ProfilePage';
import Settings from './pages/Settings';
import SingleRecipePage from './pages/SingleRecipePage';
import RecipeImportExportPage from './pages/RecipeImportExportPage';
import EditRecipePage from './pages/EditRecipePage';
import RecipeImportOptions from './pages/RecipeImportOptions';
import PasteRecipePage from './pages/PasteRecipePage';
import ImportFromURLPage from './pages/ImportFromURLPage';

// Public pages
import PublicRecipePage from './pages/PublicRecipePage';
import AccountDeleted from './pages/AccountDeleted';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import ContactPage from './pages/ContactPage';
import BlogIndexPage from './pages/BlogIndexPage';
import BlogPostPage from './pages/BlogPostPage';
import KnowledgeBasePage from './pages/KnowledgeBasePage';
import KnowledgeBaseArticle from './components/KnowledgeBaseArticle';
import LandingPage from './pages/LandingPage';
import OnboardingFlow from './components/Onboarding';

// Community pages
import CommunityGalleryPage from './pages/CommunityGalleryPage';
import CommunityRecipePage from './pages/CommunityRecipePage';
import CommunityUserProfilePage from './pages/CommunityUserProfilePage';


// iOS-compatible wrapper - Use real ProtectedRoute for proper authentication
// This ensures iOS app also shows landing page when not authenticated
const ConditionalProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Use the real ProtectedRoute which handles authentication properly
  // Allow community access for unauthenticated users
  return <ProtectedRoute allowCommunity={true}>{children}</ProtectedRoute>;
};

// Main route component
const MainRoutes: React.FC = () => {
  // Add comprehensive route debugging
  useEffect(() => {
    // console.log('====== MAINROUTES COMPONENT MOUNTED (IOSApp) ======');
    // console.log('[MainRoutes] Current URL:', window.location.href);
    // console.log('[MainRoutes] Current Hash:', window.location.hash);
    // console.log('[MainRoutes] Current pathname from router:', window.location.pathname);
    
    // Log route changes
    const handleHashChange = () => {
      // console.log('[MainRoutes] Hash changed to:', window.location.hash);
    };
    
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);
  
  return (
    <Routes>
      {/* Landing page - default route for non-authenticated users */}
      <Route path="/" element={<LandingPage />} />
      
      {/* Onboarding flow */}
      <Route path="/onboarding" element={<OnboardingFlow />} />
      
      {/* Public routes - no authentication required */}
      <Route path="/share/:id" element={<PublicRecipePage />} />
      
      {/* Signup route for Stripe payment redirect - CRITICAL FOR IOS */}
      <Route path="/signup" element={<SignupPage />} />
      
      {/* Authentication routes - inline to avoid import conflicts */}
      <Route path="/auth/*" element={
        <Routes>
          <Route path="login" element={<LoginPage />} />
          <Route path="register" element={<RegisterPage />} />
          <Route path="forgot-password" element={<ForgotPasswordPage />} />
          <Route path="verify-email" element={<VerifyEmailPage />} />
          <Route path="two-factor" element={<TwoFactorPage />} />
          <Route path="*" element={<Navigate to="/auth/login" replace />} />
        </Routes>
      } />
      
      {/* Account deleted page - accessible without auth */}
      <Route path="/account-deleted" element={<AccountDeleted />} />
      
      {/* Legal and contact pages - accessible without auth */}
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/blog" element={<BlogIndexPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/knowledge-base" element={<KnowledgeBasePage />} />
      <Route path="/knowledge-base/:category/:article" element={<KnowledgeBaseArticle />} />

      {/* Redirect /community to /app/community for compatibility */}
      <Route path="/community" element={<Navigate to="/app/community" replace />} />
      
      {/* Main app routes with iOS Tab Navigation - Protected with authentication */}
      <Route path="/app/*" element={
        <ConditionalProtectedRoute>
          <AppLayout>
            <IOSTabLayout>
              <Routes>
                <Route path="/" element={<RecipeList />} />
                <Route path="recipes" element={<RecipeList />} />
                <Route path="recipes/add" element={<RecipeImportOptions />} />
                <Route path="recipes/new" element={<CreateRecipePage />} />
                <Route path="recipes/paste" element={<PasteRecipePage />} />
                <Route path="recipes/import-url" element={<ImportFromURLPage />} />
                <Route path="recipes/edit/:id" element={<EditRecipePage />} />
                <Route path="recipes/:id" element={<SingleRecipePage />} />
                <Route path="recipes/import-export" element={<RecipeImportExportPage />} />
                <Route path="search" element={<SearchPage />} />
                <Route path="meal-plan" element={<MealPlannerPage />} />
                <Route path="shopping-list" element={<ShoppingListPage />} />
                <Route path="favorites" element={<FavoritesPage />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="settings" element={<Settings />} />

                {/* Community routes */}
                <Route path="community" element={<CommunityGalleryPage />} />
                <Route path="community/recipe/:slug" element={<CommunityRecipePage />} />
                <Route path="community/chef/:username" element={<CommunityUserProfilePage />} />
              </Routes>
            </IOSTabLayout>
          </AppLayout>
        </ConditionalProtectedRoute>
      } />
      
      {/* Redirect any unmatched routes to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export const IOSApp: React.FC = () => {
  // console.log('====== IOSAPP COMPONENT MOUNTED ======');
  // console.log('[IOSApp] Initial URL:', window.location.href);
  // console.log('[IOSApp] Initial Hash:', window.location.hash);
  
  useEffect(() => {
    // console.log('[IOSApp] useEffect - checking hash');
    // console.log('[IOSApp] Current hash:', window.location.hash);
    
    // Add a fallback hash if none exists
    if (!window.location.hash) {
      // console.log('[IOSApp] No hash found, setting to /');
      window.location.hash = '/';
    } else {
      // console.log('[IOSApp] Hash already exists, not changing');
    }
  }, []);

  
  return (
    <ErrorBoundary>
      <HashRouter>
        <ScrollToTop />
        <SecureConfigProvider>
          <AuthProviderWrapper>
            <DataProvider>
              <MainRoutes />
            </DataProvider>
          </AuthProviderWrapper>
        </SecureConfigProvider>
      </HashRouter>
    </ErrorBoundary>
  );
};

export default IOSApp;